import { FilterAltRounded, ReplayRounded, SearchRounded } from "@mui/icons-material";
import { Button, Collapse, Grid, OutlinedInput, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useState } from "react";
import * as Yup from 'yup';
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material/";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { useFilteredLegalEntityAndDealer } from "../service/useFilteredLegalEntityAndDealer";
import {areObjectsEqual} from "./FilterUtils";

interface AdvancedFilterProps {
    validationObj: any,
    fields: any,
    filters: any,
    setFilters: Function,
    mainFields?: any,
    searchInput?: any,
    setSearchInput?: Function,
    defaultValues: any,
    customDefaultValues?: any,
    onReset?: Function,
}

const AdvancedFilter = ({
    validationObj,
    fields,
    mainFields,
    searchInput,
    setSearchInput,
    filters,
    setFilters,
    defaultValues,
    customDefaultValues,
    onReset
}: AdvancedFilterProps) => {
    const { t } = useTranslation(['salesmarketing', 'translation']);
    const [advancedFilters, setAdvancedFilters] = useState(false);
    const { globalData } = useContext(GlobalContext);
    const {filteredLegalEntities,filteredDealers} = useFilteredLegalEntityAndDealer();

    const { watch, handleSubmit, control, reset, formState } = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(Yup.object(validationObj))
    });

    const watchAll = watch();
    let disabledReset = customDefaultValues && customDefaultValues ? areObjectsEqual(customDefaultValues, defaultValues) : true;


    const renderSelectedValuesByLegalEntitiesDealers =(selectedValues:any,filteredValues:any) => {
       return selectedValues.filter((selecedValue:string) => selecedValue !== 'all').map((id: any) => filteredValues.find((le: any) => le.name === id)!.name).join(', ')
    }

    return (
        <>
            <Paper elevation={1} sx={{ padding: "16px", margin: "18px" }}>
                <form onSubmit={handleSubmit(data => {
                    setAdvancedFilters(false);
                    setFilters(data);
                }, errors => console.log(errors))}>
                    <Grid container spacing={1} justifyContent={"space-between"}>
                        {setSearchInput && <Grid item xs={3}>
                            <OutlinedInput
                                fullWidth
                                inputProps={{ maxLength: 120 }}
                                value={searchInput}
                                onChange={(event) => {
                                    setSearchInput(event?.target.value)
                                }}
                                size="small"
                                startAdornment={<SearchRounded />}
                            />
                        </Grid>}
                        {!setSearchInput && mainFields &&
                            Object.keys(mainFields).map(f =>
                                <Grid item xs={3} key={f}>
                                    <Controller
                                        control={control}
                                        name={f}
                                        render={({ field, fieldState: { error }, formState }) => (
                                            mainFields[f](field, error, formState, watchAll)
                                        )}
                                    />
                                </Grid>
                            )}
                        <Grid item container xs={4} justifyContent={"flex-end"} alignItems={"center"}>
                            <Stack direction={"row"} alignItems={"center"} spacing={2}>
                                <Button disabled={(!searchInput && !formState.isDirty) && disabledReset}
                                    variant="text" size="smallPlus" color="accent"
                                    onClick={() => {
                                        if(onReset)
                                            onReset();
                                        defaultValues = customDefaultValues ? customDefaultValues : defaultValues;
                                        setFilters(defaultValues);
                                        reset(defaultValues);
                                        if (setSearchInput) {
                                            setSearchInput("");
                                        }
                                        setAdvancedFilters(false)
                                    }}>
                                    <ReplayRounded />
                                    {t('salesmarketing:buttons.reset')}
                                </Button>
                                {advancedFilters ?
                                    <Button variant="text" size="smallPlus" color="primary" onClick={() => {
                                        setAdvancedFilters(false)
                                    }}>
                                        <FilterAltRounded />
                                        {t('salesmarketing:buttons.close')}
                                    </Button>
                                    :
                                    <Button variant="text" size="smallPlus" color="accent" onClick={() => {
                                        setAdvancedFilters(true)
                                    }}>
                                        <FilterAltRounded />
                                        {t('salesmarketing:buttons.advancedFilters')}
                                    </Button>}
                                <Button type={'submit'} size={'icon'} color={'accent'}><SearchRounded /></Button>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Collapse in={advancedFilters}>
                        <Grid container spacing={1} marginTop={5}>
                            {filters.legalEntity && filters.dealer && (globalData?.user?.legalEntities?.length > 1 || globalData?.user?.dealers?.length > 1) ?
                                <Grid container item spacing={1} xs={12}>
                                    {globalData?.user?.legalEntities?.length > 1 ?
                                        <Grid item xs={3}>
                                            <Controller
                                                control={control}
                                                name={"legalEntity"}
                                                render={({ field }) => (
                                                    <TextField
                                                        fullWidth
                                                        label={t('legalEntityField')}
                                                        select
                                                        SelectProps={{
                                                            multiple: true,
                                                            renderValue: (selected: any) => renderSelectedValuesByLegalEntitiesDealers(selected,filteredLegalEntities)
                                                        }}
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    >
                                                        {filteredLegalEntities.map(((le: any) => (
                                                            <MenuItem key={le.name} value={le.name}>
                                                                <Checkbox checked={field.value.indexOf(le.name) > -1} />
                                                                <ListItemText primary={le.name} />
                                                            </MenuItem>
                                                        )))}
                                                    </TextField>
                                                )} />
                                        </Grid> : null
                                    }
                                    {globalData?.user?.dealers?.length > 1 ?
                                        <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"dealer"}
                                            render={({ field }) => (
                                                <TextField
                                                    fullWidth
                                                    label={t('dealerField')}
                                                    select
                                                    SelectProps={{
                                                        multiple: true,
                                                        renderValue: (selected: any) => renderSelectedValuesByLegalEntitiesDealers(selected,filteredDealers)
                                                    }}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                >
                                                    {filteredDealers.map(((d: any) => (
                                                        <MenuItem key={d.name} value={d.name}>
                                                            <Checkbox checked={field.value.indexOf(d.name) > -1} />
                                                            <ListItemText primary={d.name} />
                                                        </MenuItem>
                                                    )))}
                                                </TextField>
                                            )} />
                                        </Grid> : null
                                    }
                                </Grid> : null}
                            <Grid container item spacing={1} xs={12}>
                                {Object.keys(fields).map(f =>
                                    <Grid item xs={3} key={f}>
                                        <Controller
                                            control={control}
                                            name={f}
                                            render={({ field, fieldState: { error }, formState }) => (
                                                fields[f](field, error, formState, watchAll)
                                            )}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Collapse>
                </form>
            </Paper>
        </>
    )
}

export default AdvancedFilter;